/**
 * =============================================================================
 *  MOT VO SOAN CHU, DUNG CHO CA HAI DUONG - GIO CHI CON O GO
 * =============================================================================
 *
 *  Nguoi dung bao: "khi sửa chữ thì hiện lên bộ editor như chức năng textbox".
 *  Ung dung co HAI cho go chu - chu SAN CO trong tai lieu, va hop chu / ghi chu
 *  MOI - va ca hai dung CHUNG cai vo o go nay.
 *
 * =============================================================================
 *  BO DIEU KHIEN KIEU CHU DA DON HET VE BANG THONG TIN
 * =============================================================================
 *  Truoc day o go nay con cong theo mot bang day: phong chu, co chu, dam / nghieng,
 *  can le, mau chu, pham vi, ket qua chay thu, "Che chữ". Nguoi dung noi bang do
 *  qua nhieu va lap lai voi bang Thong tin. Nen TAT CA nhung dieu khien do da
 *  chuyen sang bang Thong tin (ui/PropertiesPanel.tsx, hien trong lop noi
 *  InspectorPopover). O day CHI con o go chu va mot cap nut Áp dụng / Hủy gon.
 *
 *  Cai gi hai duong khac nhau THAT - canh bao thay font, ket qua chay thu (chu
 *  san co) so voi mau chu / xoa that (hop chu moi) - deu the hien trong bang
 *  thuoc tinh o popup, khong o day.
 *
 * =============================================================================
 *  O GO VAN PHAI TRONG GIONG KET QUA
 * =============================================================================
 *  Dam / nghieng / can le / co chu / mau van duoc DAT LEN chinh o go (fontWeight,
 *  fontStyle, textAlign, fontSize, color), du cac NUT dieu khien nam o popup: bam
 *  "Đậm" trong popup ma o go khong doi la mot nut trong nhu khong lam gi. Bo ba
 *  nay va co chu tren o go la MOT phep tinh voi lop SVG ve doi tuong sau khi luu.
 *
 *  Phim tat can le (Ctrl+Shift+L/E/R) VAN chay trong o go qua `onAlignChange`,
 *  du nut can le da doi cho: nguoi dung da quen tay, va bo phim khong dam vao dau.
 */
import { useLayoutEffect, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { TextAlign } from './textEditCore';
import { editorKeyAction } from './textEditorRules';

export interface TextEditorShellProps {
  /**
   * 'inline' = sua chu san co (o soan nam de len dung chu cu).
   * 'object' = hop chu / ghi chu (o soan la mot the noi).
   *
   * Chi doi HINH THUC. Moi hanh vi - phim tat, cua chan xac nhan - giong nhau.
   */
  variant: 'inline' | 'object';
  /** Vi tri trong he toa do DOM cua trang, CSS pixel. */
  left: number;
  top: number;
  /** Be rong cua ca vo. Bo qua = de CSS quyet dinh (duong hop chu moi). */
  width?: number | undefined;
  /** Tieu de ngan: "Hộp chữ" / "Ghi chú". Bo qua voi duong sua tai cho. */
  title?: string | undefined;
  draft: string;
  onDraftChange: (text: string) => void;
  placeholder?: string | undefined;
  ariaLabel: string;
  /** Co chu tren man hinh cua o go, CSS pixel (da nhan zoom). */
  fontSizePx: number;
  colorCss: string;
  /** Chieu cao toi thieu cua o go, CSS pixel. */
  minHeightPx?: number | undefined;
  rows?: number | undefined;
  /**
   * Doi gia tri nay = DAT LAI CON TRO ve cuoi o go. Duong sua tai cho dua ma
   * pham vi vao day de con tro khong o nguyen vi tri cu tren mot chuoi khac han.
   */
  focusKey?: string | number | undefined;
  /** Lan sua nay dang bi tu choi - vien do quanh o go. */
  blocked?: boolean | undefined;

  /**
   * Kieu chu - CHI de ve o go cho trong giong ket qua. Cac NUT dieu khien nam o
   * bang thuoc tinh trong popup (ui/PropertiesPanel.tsx), khong o day.
   */
  bold: boolean;
  italic: boolean;
  /**
   * NGAN XEP CSS cua phong dang chon (vd '"PdfEditorTinos", "Times New Roman",
   * serif'). Dat len chinh o go de doi phong o popup HIEN THAY NGAY tren o soan.
   * Bo qua = giu phong mac dinh cua o (thua ke tu CSS). Day la cach chua truc
   * tiep phan nan "chọn font không có tác dụng": duong xuat da dung phong moi tu
   * truoc, nhung o go khong doi mat chu nen nguoi dung tuong khong co gi xay ra.
   */
  fontFamilyCss?: string | undefined;
  /**
   * Can le dang co. null = CHUA CHON GI (chu san co: de loi suy ra tu chu cu),
   * luc do khong ep textAlign len o go.
   */
  align: TextAlign | null;
  /**
   * Phim tat can le (Ctrl+Shift+L/E/R) trong o go goi vao day. Bo qua = phim do
   * khong lam gi. Loi phai NHAN duoc tuy chon can le thi phim moi co tac dung.
   */
  onAlignChange?: ((align: TextAlign) => void) | undefined;
  alignSupported: boolean;

  canCommit: boolean;
  commitLabel: string;
  commitTitle: string;
  onCommit: () => void;
  onCancel: () => void;
  hint: string;
  /**
   * O GO NHAN TIEU DIEM -> goi vao day. Duong sua chu dua bang Thong tin len
   * theo: nguoi dung bao "khi focus vào text area, popup thông tin nổi lên để
   * setup tùy chọn cho ô đó". Bo qua = focus khong lam gi them.
   */
  onFocus?: (() => void) | undefined;
}

export function TextEditorShell({
  variant,
  left,
  top,
  width,
  title,
  draft,
  onDraftChange,
  placeholder,
  ariaLabel,
  fontSizePx,
  colorCss,
  minHeightPx,
  rows,
  focusKey,
  blocked = false,
  bold,
  italic,
  fontFamilyCss,
  align,
  onAlignChange,
  alignSupported,
  canCommit,
  commitLabel,
  commitTitle,
  onCommit,
  onCancel,
  hint,
  onFocus,
}: TextEditorShellProps) {
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  // Dat con tro o CUOI van ban chu khong boi den toan bo. Boi den toan bo khien
  // mot phim bat ky xoa sach noi dung goc - mat mat khong hoan tac duoc bang
  // Ctrl+Z cua o soan vi no chua tung nam trong lich su cua o soan.
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [focusKey]);

  // Chan su kien chuot roi ra trang: bam vao trong o soan khong duoc coi la bam
  // chon mot khoi khac hay dat them mot ghi chu nua.
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    const action = editorKeyAction(e, { alignSupported });
    if (action.kind === 'none' || action.kind === 'newline') return;
    e.preventDefault();
    e.stopPropagation();
    switch (action.kind) {
      case 'commit':
        if (canCommit) onCommit();
        return;
      case 'cancel':
        onCancel();
        return;
      case 'align':
        onAlignChange?.(action.align);
        return;
    }
  };

  return (
    <div
      className={`inlineEditor editorShell ${variant}`}
      style={{
        left: `${left}px`,
        top: `${top}px`,
        ...(width === undefined ? {} : { width: `${Math.max(80, width)}px` }),
      }}
      onPointerDown={stop}
      onPointerUp={stop}
      onClick={stop}
    >
      {title !== undefined && <div className="editorShellTitle">{title}</div>}
      <textarea
        ref={areaRef}
        className={`inlineEditorInput${blocked ? ' blocked' : ''}`}
        value={draft}
        spellCheck={false}
        lang="vi"
        aria-label={ariaLabel}
        {...(rows === undefined ? {} : { rows })}
        {...(placeholder === undefined ? {} : { placeholder })}
        style={{
          ...(minHeightPx === undefined ? {} : { minHeight: `${minHeightPx}px` }),
          fontSize: `${Math.max(9, fontSizePx)}px`,
          lineHeight: 1.25,
          color: colorCss,
          /*
           * O GO PHAI TRONG GIONG KET QUA. Cac nut dieu khien nam o popup, nhung
           * gia tri cua chung phai duoc dat len chinh o go - neu khong nguoi dung
           * bam "Đậm" o popup ma o go khong doi. align = null nghia la CHUA CHON
           * GI (de trinh duyet suy ra tu chieu viet), nen khong ep textAlign.
           */
          fontWeight: bold ? 700 : 400,
          fontStyle: italic ? 'italic' : 'normal',
          ...(fontFamilyCss ? { fontFamily: fontFamilyCss } : {}),
          ...(align === null ? {} : { textAlign: align }),
        }}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={onKeyDown}
        {...(onFocus ? { onFocus } : {})}
      />

      <div className="inlineEditorActions">
        <span className="inlineEditorHint">{hint}</span>
        <button type="button" className="button small" onClick={onCancel}>
          Hủy
        </button>
        <button
          type="button"
          className="button small primary"
          disabled={!canCommit}
          onClick={onCommit}
          title={commitTitle}
        >
          {commitLabel}
        </button>
      </div>
    </div>
  );
}
